import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import useNotificationsStore from '@/stores/notifications'
import authApi from '@/api/auth'
import { ErrorCodes } from '@/utilities/constants'
import { getApiErrorMessage, extractErrorCode } from '@/utilities/error'
import {
  type QRCodeData,
  type VerifyOTPParams,
  type OTPData,
  type CashoutParams
} from '@/types/auth'
import config from '@/config'

const useAuthStore = defineStore('auth', () => {
  const notificationsStore = useNotificationsStore()

  const { handleErrorNotification } = notificationsStore

  const qrCodeLinkData = ref<QRCodeData | null>(null)
  const authData = ref<OTPData | null>(null)
  const isTerminalOutOfOrder = ref(true)
  const isCashoutLoading = ref(false)

  const isAuthenticated = computed(() => Boolean(authData.value?.jwt && authData.value.sessionId))

  const generateQRCode = async () => {
    try {
      const payload = { dongleId: config.dongleId }
      qrCodeLinkData.value = await authApi.generateQRCode({ payload })
      isTerminalOutOfOrder.value = false
    } catch (err) {
      handleErrorNotification(err)
      qrCodeLinkData.value = null
      isTerminalOutOfOrder.value = true
    }
  }

  const verifyOTP = async (params: VerifyOTPParams) => {
    try {
      authData.value = await authApi.verifyOTP(params)
    } catch (err) {
      const nonFatalErrors: ErrorCodes[] = [ErrorCodes.OTP_EXPIRED, ErrorCodes.OTP_INCORRECT]
      const code = extractErrorCode(err)
      const isFatal = code === null || !nonFatalErrors.includes(code)

      if (isFatal) {
        handleErrorNotification(err)
      }

      return {
        isError: true,
        isFatal,
        errorMessage: getApiErrorMessage(err)
      }
    }
  }

  const cashout = async (params: CashoutParams) => {
    try {
      isCashoutLoading.value = true
      await authApi.cashout(params)
    } catch (err) {
      handleErrorNotification(err)
    }
  }

  return {
    qrCodeLinkData,
    authData,
    isTerminalOutOfOrder,
    isAuthenticated,
    isCashoutLoading,
    generateQRCode,
    verifyOTP,
    cashout
  }
})

export default useAuthStore
